import React, { useMemo, useState } from 'react';
import { Chip, IconButton, MenuItem, Typography, Menu } from '@mui/material';
import { PatternFormat } from 'react-number-format';
import Table from '../common/Table';
import { MoreHoriz } from '@mui/icons-material';
import { GuestHelper } from '../../utils/helpers/guest.helper';
import MemberChip from 'components/common/MemberChip';
import { userRoles } from '../../utils/constants';

const INVITE_STATUS = {
    PENDING_APPROVAL: 'pending_approval',
    INVITED: 'invited'
};

const STATUS_CHIP_CONFIG = {
    [INVITE_STATUS.PENDING_APPROVAL]: {
        label: 'Awaiting approval',
        color: 'warning'
    },
    [INVITE_STATUS.INVITED]: {
        label: 'Invite sent',
        color: 'info'
    }
};

const getStatusChipConfig = status =>
    STATUS_CHIP_CONFIG[status] || { label: status || 'Pending', color: 'default' };

const InvitedMembersTable = ({
    members,
    onInvitedMemberDelete,
    onInvitedMemberApprove,
    onInvitedMemberConvert
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedMember, setSelectedMember] = useState(null);

    const handleMenuClick = (event, member) => {
        setAnchorEl(event.currentTarget);
        setSelectedMember(member);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedMember(null);
    };

    const handleDeleteMember = () => {
        if (!onInvitedMemberDelete) return;
        onInvitedMemberDelete(selectedMember);
        handleMenuClose();
    };

    const handleApproveMember = () => {
        if (!onInvitedMemberApprove) return;
        onInvitedMemberApprove(selectedMember);
        handleMenuClose();
    };

    const handleConvertInvite = () => {
        if (!onInvitedMemberConvert) return;
        onInvitedMemberConvert(selectedMember);
        handleMenuClose();
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ row }) => GuestHelper.formatPerson(row.original)
            },
            {
                Header: 'Phone',
                accessor: 'phone',
                Cell: ({ row }) => (
                    <Typography
                        sx={{ fontSize: '13px' }}
                        component="span"
                        variant="body1"
                        color="text.primary"
                    >
                        <PatternFormat
                            value={row.original?.phone?.number || ''}
                            displayType={'text'}
                            format={`+${row.original?.phone?.code} (###)-###-####`}
                        />
                    </Typography>
                )
            },
            {
                Header: 'Type',
                accessor: 'role',
                className: 'cell-center',
                Cell: ({ value }) => <MemberChip role={value} />
            },
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ row }) => {
                    const { label, color } = getStatusChipConfig(row.original?.status);
                    return <Chip size="small" label={label} color={color} />;
                }
            },
            {
                Header: 'Actions',
                className: 'cell-right',
                Cell: ({ row }) => (
                    <IconButton onClick={e => handleMenuClick(e, row.original)}>
                        <MoreHoriz />
                    </IconButton>
                )
            }
        ],
        []
    );

    const isPendingApproval =
        selectedMember?.status === INVITE_STATUS.PENDING_APPROVAL;
    const isGuest = selectedMember?.role === userRoles.GUEST;
    const isRental = selectedMember?.rental;

    return (
        <>
            <Table columns={columns} data={members} />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {isPendingApproval && onInvitedMemberApprove && (
                    <MenuItem onClick={handleApproveMember}>Approve</MenuItem>
                )}
                {isGuest && isRental && (
                    <MenuItem onClick={handleConvertInvite}>Make Tenant Invite</MenuItem>
                )}
                <MenuItem onClick={handleDeleteMember}>Delete</MenuItem>
            </Menu>
        </>
    );
};

export default InvitedMembersTable;
